import { usePortal } from "@/react/Providers/PortalProvider";
import { usePictureSets } from "@/react/Webshop/hooks/usePictureSets";
import useRoutes from "@/react/Webshop/hooks/useRoutes";
import { PRODUCT_DIGITAL_SET_UUID, usePriceList, } from "@/react/Webshop/providers/PriceListProvider";
import { useShoppingCart } from "@/react/Webshop/providers/ShoppingCartProvider";
import EpmsImage from "@/react/components/Common/EpmsImage";
import { callAPI } from "@/react/lib/common";
import { Box, Button, Card, CardContent } from "@mui/material";
import React, { useMemo } from "react";
import useCopyrightDialog from "@/react/Webshop/hooks/useCopyrightDialog";
import { Trans, useTranslation } from "react-i18next";
import { formatCurrency } from "@/react/utils";
import { Link } from "react-router-dom";
import makeStyles from "@mui/styles/makeStyles";
import { getPicturesAvailableToBeBought, getProductForType, getUuidMap, hasPortraitSiblingProduct, PICTURE_TYPES, } from "@/react/Webshop/utils/digitalPictureUtils";
import { generatePath } from "react-router";
const useStyles = makeStyles((theme) => ({
    favoriteImageContainer: {
        width: "100px",
        height: "135px",
        border: "1px solid #B5B5B57F",
        marginRight: "16px",
        backgroundColor: "#B5B5B5AF",
        flexShrink: 0,
        [theme.breakpoints.up("sm")]: {
            width: "150px",
            height: "202px",
        },
        "& > img": {
            objectFit: "cover",
            width: "100%",
            height: "100%",
        },
    },
    subjectName: {
        fontSize: "15px",
        fontWeight: 500,
    },
    pictureList: {
        margin: 0,
        padding: 0,
        listStyle: "none",
        overflowX: "auto",
        display: "flex",
        "& > li": {
            height: "50px",
            marginRight: theme.spacing(1),
            display: "inline-block",
            border: "1px solid #b5b5b5",
            "& > img": {
                height: "100%",
            },
            [theme.breakpoints.up("sm")]: {
                height: "100px",
            },
        },
    },
    pictureListContainer: {
        width: "100%",
        overflow: "hidden",
    },
    card: {
        marginBottom: theme.spacing(2),
    },
    infoAndPicturesContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minWidth: 0,
    },
    cardActions: {
        padding: theme.spacing(2),
        "& a, & button": {
            fontWeight: "bold",
        },
    },
    textContainer: {
        display: "flex",
        justifyContent: "space-between",
    },
    productDescription: {
        color: theme.palette.primary.main,
        "& h3": {
            color: "black",
            fontSize: "15px",
            margin: 0,
        },
    },
    oldPrice: {
        color: "rgba(0, 0, 0, 0.6)",
        textDecoration: "line-through",
        textAlign: "right",
    },
    currentPrice: {
        color: theme.palette.secondary.main,
        fontWeight: "bold",
        fontSize: "16px",
        textAlign: "right",
    },
    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    rightButton: {
        paddingRight: 0,
    },
}));
const PictureSetItem = ({ pictureSet }) => {
    const { update: updatePictureSets } = usePictureSets();
    const { subject } = pictureSet;
    const classes = useStyles();
    const routes = useRoutes();
    const { updateCart, qualifyForDigitalRebate } = useShoppingCart();
    const portal = usePortal();
    const { digitalRebate } = portal.webshop;
    const { t } = useTranslation();
    const priceList = usePriceList();
    const { getCopyrightTriggerProps, copyrightDialog } = useCopyrightDialog();
    const buyablePictures = useMemo(() => {
        return getPicturesAvailableToBeBought(pictureSet, priceList, t);
    }, [pictureSet, priceList, t]);
    // This is only toggle for the picture set overview page. It's only available if
    // the only digital product is portrait individual sets
    const toggleSetInCart = () => {
        const ps = pictureSet;
        return () => {
            callAPI("/ajax/cart/toggle_digital_set_in_cart.php", "POST", {
                pictureSet: ps.subject.uuid,
                type: "IP",
            }).then((data) => {
                if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
                    window.epmsAnalytics.onCartChange(data);
                }
                updatePictureSets();
                updateCart();
            });
        };
    };
    const availableDigitalProducts = useMemo(() => {
        const availableDigitalProducts = [];
        const portraitSiblingProductAvailable = hasPortraitSiblingProduct(priceList);
        const settings = getUuidMap(portraitSiblingProductAvailable, t);
        for (const type of PICTURE_TYPES) {
            const setProduct = getProductForType(priceList, settings[type], "pictureSet");
            if (setProduct) {
                availableDigitalProducts.push(setProduct);
            }
            const fileProduct = getProductForType(priceList, settings[type], "singleFile");
            if (fileProduct) {
                availableDigitalProducts.push(fileProduct);
            }
        }
        return availableDigitalProducts;
    }, [priceList, t]);
    const portraitSetIsOnlyAvailableDigitalProduct = availableDigitalProducts.length === 1 &&
        availableDigitalProducts[0].uuid === PRODUCT_DIGITAL_SET_UUID;
    const favoritePicture = useMemo(() => {
        return buyablePictures.find((picture) => picture.keywords.indexOf("favorite") >= 0);
    }, [buyablePictures]);
    const pictures = useMemo(() => {
        return buyablePictures.filter((picture) => {
            return picture.uuid !== (favoritePicture === null || favoritePicture === void 0 ? void 0 : favoritePicture.uuid);
        });
    }, [buyablePictures, favoritePicture === null || favoritePicture === void 0 ? void 0 : favoritePicture.uuid]);
    let rebatedSetPrice = null;
    if (qualifyForDigitalRebate && digitalRebate.portraitSetPrice > 0) {
        rebatedSetPrice = digitalRebate.portraitSetPrice;
    }
    return (React.createElement(Card, { classes: { root: classes.card } },
        React.createElement(CardContent, null,
            React.createElement(Box, { display: "flex" },
                React.createElement("div", { className: classes.favoriteImageContainer }, favoritePicture && (React.createElement(EpmsImage, Object.assign({}, getCopyrightTriggerProps(), { uuid: favoritePicture.uuid })))),
                React.createElement("div", { className: classes.infoAndPicturesContainer },
                    React.createElement("div", null,
                        React.createElement("div", null, pictureSet.year),
                        React.createElement("div", { className: classes.subjectName }, subject.name),
                        React.createElement("div", null, subject.group)),
                    React.createElement("div", { className: classes.pictureListContainer },
                        React.createElement("ul", { className: classes.pictureList }, pictures.map((picture) => {
                            return (React.createElement("li", { key: picture.uuid },
                                React.createElement(EpmsImage, Object.assign({}, getCopyrightTriggerProps(), { uuid: picture.uuid }))));
                        })))))),
        React.createElement("div", { className: classes.cardActions },
            portraitSetIsOnlyAvailableDigitalProduct && (React.createElement("div", null,
                React.createElement("div", { className: classes.textContainer },
                    React.createElement("div", { className: classes.productDescription },
                        React.createElement("h3", null,
                            React.createElement(Trans, null, "Digital portrait set")),
                        React.createElement("div", null,
                            React.createElement(Trans, { count: pictures.length, tOptions: {
                                    defaultValue_plural: "{{numPictures}} portrait pictures",
                                } },
                                { numPictures: pictures.length },
                                " portrait picture"))),
                    React.createElement("div", null,
                        rebatedSetPrice && (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: classes.oldPrice }, formatCurrency(availableDigitalProducts[0].productprices[0].price)),
                            React.createElement("div", { className: classes.currentPrice }, formatCurrency(rebatedSetPrice)))),
                        !rebatedSetPrice && (React.createElement("div", { className: classes.currentPrice }, formatCurrency(availableDigitalProducts[0].productprices[0].price))))),
                React.createElement(Box, { display: "flex", justifyContent: "space-between", mt: 2 },
                    React.createElement(Button, { color: "primary", component: Link, to: generatePath(routes["products.digitalportraits.subject"].path, { subjectUuid: subject.uuid }), classes: { root: classes.rightButton } },
                        React.createElement(Trans, null, "View pictures")),
                    React.createElement(Button, { color: "primary", onClick: toggleSetInCart(), classes: { root: classes.rightButton } }, pictureSet.digitalSet.IP.inCart
                        ? t("Remove from cart")
                        : t("Buy portrait set"))))),
            !portraitSetIsOnlyAvailableDigitalProduct && (React.createElement("div", null,
                React.createElement(Box, { display: "flex", justifyContent: "flex-end" },
                    React.createElement(Button, { color: "primary", component: Link, to: generatePath(routes["products.digitalportraits.subject"].path, { subjectUuid: subject.uuid }), classes: { root: classes.rightButton } },
                        React.createElement(Trans, null, "Choose pictures")))))),
        copyrightDialog));
};
export default PictureSetItem;
